import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { setLayoutDashboardTitle } from "../../../store/ui-slice";
import useIsMobile from "../../../hooks/useIsMobile";
import VerifAuthorizationDialog from "../../../dialogs/VerifAuthorizationDialog";
import { useAPIEditPermintaanMinyak } from "../api/api-edit-permintaan-minyak";
import { useAPIVerifPermintaanMinyak } from "../api/api-verify-permintaan-minyak";
import {
  SIGN_SECTION,
  getProgressText,
  getSectionStages,
  getSectionSummary,
  getStatusInfo,
} from "../utils/permintaan-minyak-helpers";
import PermintaanMinyakStepper, {
  SECTION_STEPS,
} from "./permintaan-minyak-stepper";
import SectionPengajuan from "./sections/section-pengajuan";
import SectionAnalisis from "./sections/section-analisis";
import SectionPembelian from "./sections/section-pembelian";
import SectionPenerimaan from "./sections/section-penerimaan";
import SectionKelengkapan from "./sections/section-kelengkapan";

const title = "Permintaan Minyak";

/**
 * Form TRAN-DOC-003 as a page: /transaksi/permintaan-minyak/edit/:id ("new" for a new request).
 * The sections are saved and signed one after the other.
 */
const PermintaanMinyakEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [openSection, setOpenSection] = useState("pengajuan");
  const createdIdRef = useRef(null);

  const {
    loading,
    data,
    setData,
    error,
    fetchDataOrCreateNew,
    reload,
    saveSection,
  } = useAPIEditPermintaanMinyak();

  const {
    loading: verifLoading,
    verifSetting,
    fetchVerifSetting,
    verify,
  } = useAPIVerifPermintaanMinyak();

  // use effect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title }));
    fetchVerifSetting();
  }, []);

  useEffect(() => {
    if (id === "new") {
      fetchDataOrCreateNew({});
    } else if (data.id !== Number(id)) {
      // not when the request was just created from this page
      fetchDataOrCreateNew({ id: Number(id) });
    }
  }, [id]);

  // handle ui event section

  // a new request gets its id on the first save: move the url to it
  const saveAndSync = async (section, options) => {
    const wasNew = !data.id;
    const saved = await saveSection(section, options);

    if (saved && wasNew && saved.id) {
      // the signature that follows a save has the old render: it needs this id
      createdIdRef.current = saved.id;
      navigate(`../${saved.id}`, { replace: true });
    }
    return saved;
  };

  // check the section of the signature before it is signed; it is saved only when it was edited
  const handleOnValidateSign = async (name) => {
    const { section, strict } = SIGN_SECTION[name];
    return await saveAndSync(section, { strict, skipIfUnchanged: true });
  };

  // the dialog of the authorization closes when the result is truthy
  const handleOnSign = async (name, confirmProps) => {
    const documentId = data.id ?? createdIdRef.current;
    const result = await verify({
      id: documentId,
      name,
      username: confirmProps?.username,
      password: confirmProps?.password,
    });

    if (result) {
      await reload(documentId);
    }
    return result;
  };

  const handleOnClickBack = () => {
    navigate("../..");
  };

  const stages = getSectionStages(data);
  const statusInfo = getStatusInfo(data.status);
  const doneCount = SECTION_STEPS.filter(
    (step) => stages[step.key] === "done",
  ).length;

  const signProps = {
    verifSetting,
    onValidateSign: handleOnValidateSign,
    onSign: handleOnSign,
  };

  // the section being worked on; when everything is signed, the last one that was closed
  const activeSection =
    SECTION_STEPS.find((step) => stages[step.key] === "active")?.key ??
    [...SECTION_STEPS].reverse().find((step) => stages[step.key] === "done")
      ?.key ??
    "pengajuan";

  // only one section is open: the active one, until the user opens another
  useEffect(() => {
    setOpenSection(activeSection);
  }, [activeSection, data.id]);

  const handleOnSelectStep = (key) => {
    setOpenSection(key);
    setTimeout(() => {
      document
        .getElementById(`section-${key}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const sectionProps = (section) => ({
    data,
    setData,
    error,
    stage: stages[section],
    saving: loading,
    onSave: () => saveAndSync(section),
    signProps,
    cardProps: {
      id: `section-${section}`,
      last: section === SECTION_STEPS[SECTION_STEPS.length - 1].key,
      open: openSection === section,
      onToggle: () =>
        setOpenSection((prev) => (prev === section ? null : section)),
      summary: getSectionSummary(section, data, stages[section]),
    },
  });

  // photos are stored by the date of the request: permintaan_minyak/<yyyy>/<MM>/<id>
  const imageFolder = `permintaan_minyak/${format(
    data.tanggal ? new Date(data.tanggal) : new Date(),
    "yyyy/MM",
  )}/${data.id ?? "new"}`;

  return (
    <div className="col-start-start relative p-4 gap-4 w-100">
      <ConfirmDialog />
      <VerifAuthorizationDialog loading={verifLoading} />

      {/* header section */}
      <div className="elevated-card-tight-vertical col-start-start w-full gap-3">
        <div className="row-between-center w-full gap-2">
          <div className="row-start-center min-w-0 gap-2">
            <Button
              icon="pi pi-arrow-left"
              label={isMobile ? undefined : "Kembali"}
              rounded={isMobile}
              outlined={!isMobile}
              text={isMobile}
              aria-label="Kembali"
              onClick={handleOnClickBack}
            />
            <div className="col-start-start min-w-0">
              <span className="truncate font-bold text-indigo-500">
                {data.nomor || "Permintaan baru"}
              </span>
              <span className="text-xs text-slate-400">{title}</span>
            </div>
          </div>
          <div className="row-start-center gap-1">
            <Button
              icon="pi pi-refresh"
              rounded
              text
              aria-label="Muat ulang"
              disabled={!data.id}
              onClick={() => reload()}
            />
          </div>
        </div>

        {data.id && (
          <>
            <div className="row-start-center w-full flex-wrap gap-x-3 gap-y-1">
              <Tag
                className="whitespace-nowrap"
                value={statusInfo.label}
                severity={statusInfo.severity}
              />
              {(data.data_department || data.data_jenis_minyak) && (
                <div className="row-start-center min-w-0 gap-2 text-sm text-slate-500">
                  <i className="pi pi-building text-slate-400" />
                  <span className="truncate">
                    {[data.data_department?.nama, data.data_jenis_minyak?.nama]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              )}
            </div>

            {/* the litres of the request, step by step */}
            <div className="grid w-full grid-cols-3 divide-x divide-slate-100 rounded-md bg-slate-50 py-2">
              {[
                { label: "Diminta", value: data.jumlah_diminta },
                { label: "Disetujui", value: data.jumlah_disetujui },
                { label: "Diterima", value: data.jumlah_diterima },
              ].map((item) => (
                <div
                  key={item.label}
                  className="col-center-center gap-0.5 px-2"
                >
                  <span className="text-xs font-semibold text-slate-400">
                    {item.label}
                  </span>
                  <span className="font-bold text-slate-600">
                    {Number(item.value) > 0
                      ? `${Number(item.value).toLocaleString("id-ID", {
                          maximumFractionDigits: 2,
                        })} L`
                      : "-"}
                  </span>
                </div>
              ))}
            </div>

            <div className="col-start-start w-full gap-1">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${
                    data.status === -1 ? "bg-red-400" : "bg-emerald-500"
                  }`}
                  style={{
                    width: `${(doneCount / SECTION_STEPS.length) * 100}%`,
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-slate-400">
                {getProgressText(data)}
              </span>
            </div>
          </>
        )}
      </div>

      {data.status === -1 && (
        <div className="row-start-center w-full gap-2 rounded bg-red-50 px-3 py-2 text-red-600">
          <i className="pi pi-times-circle" />
          <span className="font-semibold">
            Permintaan ditolak
            {data.alasan_penolakan ? `: ${data.alasan_penolakan}` : "."}
          </span>
        </div>
      )}

      {/* progress: on a phone the rail of the sections shows it */}
      {!isMobile && (
        <PermintaanMinyakStepper
          stages={stages}
          openSection={openSection}
          onSelect={handleOnSelectStep}
        />
      )}

      {/* sections: on a phone they touch, so the rail runs from one to the next */}
      <div className={`col-start-start w-full ${isMobile ? "" : "gap-4"}`}>
        <SectionPengajuan {...sectionProps("pengajuan")} />
        <SectionAnalisis {...sectionProps("analisis")} />
        <SectionPembelian
          {...sectionProps("pembelian")}
          imageFolder={imageFolder}
        />
        <SectionPenerimaan {...sectionProps("penerimaan")} />
        <SectionKelengkapan {...sectionProps("kelengkapan")} />
      </div>

      {(loading || verifLoading) && (
        <div
          className={`col-center-center gap-2 absolute inset-0 z-100 bg-black/20 rounded`}
        >
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
};

export default PermintaanMinyakEditor;
